/**
 * I18n Debug Utilities
 *
 * Provides utilities for managing i18n debug mode via query string and localStorage.
 *
 * Usage:
 * - Add `?i18n=debug` to URL to enable debug mode (persists to localStorage)
 * - Add `?i18n=default` to URL to disable debug mode (clears localStorage)
 * - Debug mode wraps all translations with brackets: [Hello World]
 * - Missing translations always show with warning: ⚠️key.name⚠️
 */

const I18N_DEBUG_STORAGE_KEY = "i18n.debug";
const I18N_QUERY_PARAM = "i18n";
const I18N_DEBUG_VALUE = "debug";
const I18N_DEFAULT_VALUE = "default";

function isBrowser(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

function getI18nQueryParam(): string | null {
  if (!isBrowser()) {
    return null;
  }

  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(I18N_QUERY_PARAM);
}

function getDebugFromStorage(): boolean {
  if (!isBrowser()) {
    return false;
  }

  return window.localStorage.getItem(I18N_DEBUG_STORAGE_KEY) === "true";
}

function setDebugInStorage(enabled: boolean): void {
  if (!isBrowser()) {
    return;
  }

  if (enabled) {
    window.localStorage.setItem(I18N_DEBUG_STORAGE_KEY, "true");
  } else {
    window.localStorage.removeItem(I18N_DEBUG_STORAGE_KEY);
  }
}

/**
 * Resolve i18n debug mode from query string and localStorage.
 *
 * Priority:
 * 1. Query string `?i18n=debug` enables debug mode and persists to localStorage
 * 2. Query string `?i18n=default` disables debug mode and clears localStorage
 * 3. Otherwise, uses value from localStorage
 *
 * @returns Whether debug mode should be enabled
 */
export function resolveI18nDebugMode(): boolean {
  const queryParam = getI18nQueryParam();

  if (queryParam === I18N_DEBUG_VALUE) {
    setDebugInStorage(true);
    return true;
  }

  if (queryParam === I18N_DEFAULT_VALUE) {
    setDebugInStorage(false);
    return false;
  }

  return getDebugFromStorage();
}
